import { useEffect, useLayoutEffect, useMemo, useState } from "react";

export interface Size {
  height: number;
  width: number;
}

export function useSize(element: HTMLElement | null): Size | undefined {
  const [size, setSize] = useState<Size | undefined>(undefined);

  useLayoutEffect(() => {
    if (!element) {
      setSize(undefined);
      return;
    }
    // Observe the element so consumers track size changes that don't coincide
    // with a re-render — e.g. an anchor whose width animates. Read
    // clientWidth/clientHeight (padding-box) to preserve the prior measurement.
    const measure = () =>
      setSize({ height: element.clientHeight, width: element.clientWidth });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return size;
}

export function useRefSize<E extends HTMLElement>(): [
  React.RefCallback<E>,
  Size | undefined,
] {
  const [element, setElement] = useState<E | null>(null);
  const [height, setHeight] = useState<number | undefined>();
  const [width, setWidth] = useState<number | undefined>();

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setHeight(entry?.contentRect.height);
      setWidth(entry?.contentRect.width);
    });

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
        setHeight(undefined);
        setWidth(undefined);
      }
    };
  }, [element]);

  return [
    setElement,
    useMemo(
      () =>
        height !== undefined && width !== undefined
          ? { height, width }
          : undefined,
      [height, width],
    ),
  ];
}
